import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Building2, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PublicNav from "@/components/PublicNav";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TYPE_CONFIG = {
  facility: {
    label: "Facility / Community",
    icon: Building2,
    badge: "Facility Application — CareHomeConnect Network",
    headline: "Let's start with your community.",
    subheadline: "Every application is reviewed personally. No templates, no bots.",
    entityLabel: "Community name",
    entityPlaceholder: "Sunridge Senior Living",
  },
  placement_director: {
    label: "Placement Director / Advisor",
    icon: Briefcase,
    badge: "Placement Director Application — CareHomeConnect Network",
    headline: "Let's start with your practice.",
    subheadline: "We review every placement professional personally. Real people, real conversations.",
    entityLabel: "Agency or practice name",
    entityPlaceholder: "Smith Senior Advisors",
  },
};

export default function PartnerApply() {
  const [searchParams] = useSearchParams();
  const applicantType = searchParams.get("type") === "placement_director" ? "placement_director" : "facility";
  const cfg = TYPE_CONFIG[applicantType];

  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [textAnswer, setTextAnswer] = useState("");
  const [applicationId, setApplicationId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({
    contact_name: "",
    contact_title: "",
    email: "",
    phone: "",
    community_name: "",
  });

  useEffect(() => {
    api
      .get("/quiz/partner", { params: { type: applicantType } })
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => {});
  }, [applicantType]);

  const totalSteps = questions.length + 1;
  const progress = Math.min(100, (step / totalSteps) * 100);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.contact_name.trim() || !contact.email.trim() || !contact.community_name.trim()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/partners/apply/start", { ...contact, applicant_type: applicantType });
      setApplicationId(data.application_id);
      setStep(1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const selectOption = (qid, value) => {
    setAnswers((p) => ({ ...p, [qid]: value }));
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const submitTextAndAdvance = (qid) => {
    if (textAnswer.trim()) setAnswers((p) => ({ ...p, [qid]: textAnswer.trim() }));
    setTextAnswer("");
    setStep((s) => s + 1);
  };

  const finalize = async () => {
    setLoading(true);
    try {
      await api.post("/partners/apply/submit", { application_id: applicationId, answers });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when we've passed all questions
  useEffect(() => {
    if (applicationId && step > questions.length && !submitted && !loading) {
      finalize();
    }
    // eslint-disable-next-line
  }, [step]);

  const currentQ = step >= 1 && step <= questions.length ? questions[step - 1] : null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Seo title="Application received" path="/for-partners/apply" noindex />
        <PublicNav />
        <section className="max-w-2xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-20" data-testid="partner-apply-success">
          <div className="card-soft p-10 md:p-14 text-center">
            <div className="inline-flex w-16 h-16 rounded-full bg-[#9CAF88]/15 items-center justify-center mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#2C4C3B]" />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-5">
              <Award className="w-3.5 h-3.5" /> Application received
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] tracking-tight leading-tight">
              Thank you. We'll take it from here.
            </h1>
            <p className="mt-5 text-[#5C6B62] leading-relaxed max-w-lg mx-auto">
              Our partnerships team reviews every application personally. If your {applicantType === "placement_director" ? "practice" : "community"} looks like a fit for the
              families we serve, someone from our team will reach out — typically within <strong className="text-[#2C4C3B]">3 business days</strong>.
            </p>
            <p className="mt-4 text-sm text-[#5C6B62] max-w-md mx-auto">
              We protect the integrity of our network by accepting a limited number of partners per region. If now isn't the right moment, that's okay — we'll note your interest for future review.
            </p>
            <div className="mt-10 text-xs text-[#5C6B62]">
              In the meantime, read what families using CareHomeConnect are experiencing.
            </div>
            <Link to="/articles">
              <Button className="mt-4 bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 px-8 font-medium" data-testid="apply-success-articles">
                Explore our library
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo title="Apply to partner with CareHomeConnect" description="Apply to join CareHomeConnect's network of trusted senior living partners." path="/for-partners/apply" noindex />
      <PublicNav />
      <div className="max-w-2xl mx-auto px-6 md:px-8 pt-8 md:pt-16 pb-24">
        <Link to="/for-partners" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6">
          <ArrowLeft className="w-4 h-4" /> About our network
        </Link>

        <div className="mb-10">
          <div className="flex items-center justify-between text-xs text-[#5C6B62] mb-2">
            <span>Step {Math.min(step + 1, totalSteps)} of {totalSteps}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 bg-[#E2DFD8] rounded-full overflow-hidden" data-testid="partner-quiz-progress">
            <div className="h-full bg-[#9CAF88] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {step === 0 && (
          <div className="animate-fade-in-up" data-testid="partner-apply-contact">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-4">
                <cfg.icon className="w-3.5 h-3.5" /> {cfg.badge}
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] tracking-tight">
                {cfg.headline}
              </h1>
              <p className="mt-3 text-[#5C6B62]">
                {cfg.subheadline}
              </p>
            </div>

            <form onSubmit={submitContact} className="card-soft p-6 md:p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name" className="text-[#2C4C3B] font-medium">Your name</Label>
                  <Input
                    id="contact_name"
                    data-testid="apply-contact-name"
                    value={contact.contact_name}
                    onChange={(e) => setContact({ ...contact, contact_name: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contact_title" className="text-[#2C4C3B] font-medium">
                    Your title <span className="text-xs text-[#5C6B62] font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="contact_title"
                    data-testid="apply-contact-title"
                    value={contact.contact_title}
                    onChange={(e) => setContact({ ...contact, contact_title: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                    placeholder={applicantType === "placement_director" ? "Senior Advisor" : "Executive Director"}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="community_name" className="text-[#2C4C3B] font-medium">{cfg.entityLabel}</Label>
                <Input
                  id="community_name"
                  data-testid="apply-community-name"
                  value={contact.community_name}
                  onChange={(e) => setContact({ ...contact, community_name: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                  placeholder={cfg.entityPlaceholder}
                  required
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[#2C4C3B] font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="apply-email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#2C4C3B] font-medium">
                    Phone <span className="text-xs text-[#5C6B62] font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="apply-phone"
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 font-medium"
                data-testid="apply-contact-submit"
              >
                {loading ? "Just a moment…" : "Continue application"}
                {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>

              <div className="flex items-center gap-1.5 text-xs text-[#5C6B62] justify-center pt-2">
                <ShieldCheck className="w-3.5 h-3.5 text-[#9CAF88]" />
                Your application is confidential and reviewed only by our partnerships team.
              </div>
            </form>
          </div>
        )}

        {currentQ && currentQ.type !== "text" && (
          <div className="animate-fade-in-up" key={currentQ.id} data-testid={`partner-quiz-q-${currentQ.id}`}>
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] tracking-tight mb-6">
              {currentQ.label}
            </h2>
            <div className="space-y-3">
              {currentQ.options?.map((opt) => {
                const selected = answers[currentQ.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => selectOption(currentQ.id, opt.value)}
                    data-testid={`partner-quiz-option-${currentQ.id}-${opt.value}`}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all font-medium ${
                      selected
                        ? "border-[#2C4C3B] bg-[#9CAF88]/15 text-[#1A2E24]"
                        : "border-[#E2DFD8] bg-white text-[#2C4C3B] hover:border-[#9CAF88] hover:bg-[#9CAF88]/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {!currentQ.required && (
              <button
                onClick={() => setStep(step + 1)}
                className="mt-6 text-sm text-[#5C6B62] hover:text-[#2C4C3B] underline"
              >
                Skip this question
              </button>
            )}
          </div>
        )}

        {currentQ && currentQ.type === "text" && (
          <div className="animate-fade-in-up" key={currentQ.id}>
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] tracking-tight mb-6">
              {currentQ.label}
            </h2>
            <Textarea
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.target.value)}
              rows={5}
              className="rounded-2xl bg-white border-[#E2DFD8] p-5 text-base"
              placeholder="Share what makes your community stand out…"
              data-testid={`partner-quiz-text-${currentQ.id}`}
            />
            <div className="mt-4 flex gap-3">
              <Button
                onClick={() => submitTextAndAdvance(currentQ.id)}
                className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 px-6"
                data-testid={`partner-quiz-text-submit-${currentQ.id}`}
              >
                Finish application <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={() => setStep(step + 1)} className="rounded-xl h-12 px-6 text-[#5C6B62]">
                Skip
              </Button>
            </div>
          </div>
        )}

        {step > questions.length && !submitted && (
          <div className="text-center py-20" data-testid="partner-apply-finalizing">
            <div className="inline-block w-12 h-12 border-4 border-[#9CAF88]/30 border-t-[#2C4C3B] rounded-full animate-spin mb-6" />
            <p className="text-[#2C4C3B] font-medium">Submitting your application…</p>
          </div>
        )}
      </div>
    </div>
  );
}
